<script lang="ts" setup>
import { computed, watch } from "vue";
import { useRoute } from "vue-router";
import { useProducts, useProduct } from "@/queries/products";
import { useRecentlyViewed } from "@/composables/useRecentlyViewed";

import PanelGrid from "@/views/shared/components/PanelGrid.vue";
import PanelCard from "@/views/shared/components/PanelCard.vue";
import PublisherDashboard from "./components/PublisherDashboard.vue";
import ProductDetailView from "./components/ProductDetailView.vue";
import PublisherProducts from "./components/PublisherProducts.vue";
import PublisherSubscribers from "./components/PublisherSubscribers.vue";
import PublisherAnalytics from "./components/PublisherAnalytics.vue";

const route = useRoute();

const { productsError: error } = useProducts();
const { trackView } = useRecentlyViewed();

const currentView = computed(() => {
  const routeName = route.name as string;

  if (route.params.productId) return "product-detail";

  switch (routeName) {
    case "publisher-products":
      return "products";
    case "publisher-subscribers":
      return "subscribers";
    case "publisher-analytics":
      return "analytics";
    default:
      return "dashboard";
  }
});

const selectedProductId = computed(() => route.params.productId as string);

// useProduct auto-fetches reactively when selectedProductId changes
const { product, productError } = useProduct(selectedProductId);

// Track recently viewed when a product loads
watch(product, (p) => {
  if (p?.id) {
    trackView({
      id: p.id,
      name: p.name,
      type: "product",
      route: `/publisher/${p.id}`,
      description: p.description,
      icon: "bi-grid",
      metadata: {
        masterProjectId: p.masterProjectId,
      },
    });
  }
});
</script>

<template>
  <main>
    <!-- Main Loader & Error -->
<!--    <div v-if="loading" class="overlay">-->
<!--      <div class="overlay-content">-->
<!--        <div class="spinner-border" role="status">-->
<!--          <span class="visually-hidden">Loading...</span>-->
<!--        </div>-->
<!--      </div>-->
<!--    </div>-->

    <PanelGrid :columns="1">
      <PanelCard>
        <!-- Dynamic Component Rendering -->
        <PublisherDashboard v-if="currentView === 'dashboard'" :error="error" />
        <ProductDetailView
          v-else-if="currentView === 'product-detail'"
          :productId="selectedProductId"
          :error="productError"
        />
        <PublisherProducts
          v-else-if="currentView === 'products'"
          :error="error"
        />
        <PublisherSubscribers
          v-else-if="currentView === 'subscribers'"
          :error="error"
        />
        <PublisherAnalytics
          v-else-if="currentView === 'analytics'"
          :error="error"
        />
      </PanelCard>
    </PanelGrid>
  </main>
</template>
